import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmt, Step } from "./utils";
import { VocePicker } from "./VocePicker";

export function RipartoWizard({ id_doc, totale, onClose }) {
  const [importo, setImporto] = useState(Number(totale || 0));
  const [conto, setConto] = useState(null);
  const [voceDescr, setVoceDescr] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tipi, setTipi] = useState([]);
  const [ripartoTipo, setRipartoTipo] = useState("");
  const [preview, setPreview] = useState(null);
  const [eseguendo, setEseguendo] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { api.get("/pn/riparto/tipi").then(r => setTipi(r.data.data || [])); }, []);

  useEffect(() => {
    if (!ripartoTipo || !importo) { setPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const params = { tipo: ripartoTipo, importo: Number(importo) };
        if (conto) params.conto = Number(conto);
        const r = await api.get("/pn/riparto/preview", { params });
        setPreview(r.data); setErr(null);
      } catch (e) {
        setPreview(null); setErr(e.response?.data?.detail || "Errore preview");
      }
    }, 200);
    return () => clearTimeout(t);
  }, [ripartoTipo, conto, importo]);

  const esegui = async () => {
    if (!conto || !ripartoTipo) return;
    setEseguendo(true); setErr(null);
    try {
      await api.post(`/pn/fatture/${id_doc}/riparto/apply`, {
        conto: Number(conto), riparto_tipo: ripartoTipo, somma_da_ripartire: Number(importo),
      });
      onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Errore esecuzione"); }
    finally { setEseguendo(false); }
  };

  const canExecute = conto && ripartoTipo && importo > 0 && preview && Math.abs(preview.totale_quote - Number(importo)) < 0.01;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} data-testid="pn-rip-modal"
        style={{ background: "white", width: 920, maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ background: "#0288d1", color: "white", padding: "8px 14px", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14 }}>⚡ RIPARTO COSTO — Wizard</span>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "white", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12, flex: 1, overflow: "auto" }}>
          {err && <div style={{ padding: 8, background: "#fff0f0", color: "#c00", fontSize: 12, fontWeight: 700 }}>{err}</div>}
          <Step n={1} title="Importo da ripartire" done={importo > 0}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "#666" }}>Default: totale fattura. Puoi modificarlo.</span>
              <div style={{ display: "flex", alignItems: "center", border: "2px solid #0288d1", background: "#e1f5fe", height: 44, padding: "0 12px" }}>
                <span style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 800, color: "#0d47a1", marginRight: 6 }}>€</span>
                <input type="text" inputMode="decimal" value={importo} onChange={e => { const v = e.target.value.replace(/[^\d.,-]/g, "").replace(",", "."); setImporto(v); }}
                  data-testid="pn-rip-importo"
                  style={{ width: 140, border: "none", outline: "none", fontSize: 22, fontWeight: 800, fontFamily: "monospace", textAlign: "right", background: "transparent", color: "#0d47a1" }} />
              </div>
            </div>
          </Step>
          <Step n={2} title="Voce di costo (Bilancio)" done={!!conto}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button onClick={() => setPickerOpen(true)} data-testid="pn-rip-voce-pick"
                style={{ padding: "6px 16px", background: conto ? "#0d47a1" : "#0288d1", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                🌲 {conto ? `[${conto}] cambia voce` : "Scegli voce di bilancio"}
              </button>
              {conto && voceDescr && (
                <div style={{ padding: "6px 12px", background: "#e8f5e9", border: "1px solid #4caf50", fontSize: 12, fontWeight: 700, color: "#1b5e20" }}>
                  ✓ <span style={{ fontFamily: "monospace" }}>[{conto}]</span> {voceDescr}
                </div>
              )}
            </div>
          </Step>
          <Step n={3} title="Tabella riparto" done={!!ripartoTipo}>
            <select value={ripartoTipo} onChange={e => setRipartoTipo(e.target.value)} data-testid="pn-rip-tipo"
              style={{ padding: "6px 10px", border: "1px solid #888", fontSize: 12, minWidth: 280, color: "#0d47a1", background: "white", fontWeight: 600 }}>
              <option value="">— scegli un riparto —</option>
              {tipi.map(t => (
                <option key={t.tipo} value={t.tipo} disabled={!t.popolato}>
                  {t.tipo} {!t.popolato ? "(non popolato)" : ""}
                </option>
              ))}
            </select>
          </Step>
          {preview && (
            <Step n={4} title="Simulazione e suddivisione">
              <table className="sw-table sw-table-compact" style={{ fontSize: 11, width: "100%" }}>
                <thead style={{ background: "#0288d1", color: "white" }}>
                  <tr>
                    <th style={{ width: 110 }}>SETTORE</th>
                    <th style={{ width: 120, textAlign: "right" }}>BASE 1000</th>
                    <th style={{ width: 110, textAlign: "right" }}>%</th>
                    <th style={{ width: 60, textAlign: "center" }}>CONTO</th>
                    <th>VOCE BILANCIO</th>
                    <th style={{ width: 130, textAlign: "right" }}>QUOTA €</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.simulazione.map(s => (
                    <tr key={s.plesso}>
                      <td style={{ fontWeight: 700, color: "#0d47a1" }}>{s.plesso}</td>
                      <td style={{ textAlign: "right", fontFamily: "monospace", color: "#555" }}>{Number(s.base_1000).toLocaleString("it-IT")}</td>
                      <td style={{ textAlign: "right", fontFamily: "monospace" }}>{s.percentuale.toFixed(4)}%</td>
                      <td style={{ textAlign: "center", fontFamily: "monospace", color: "#0288d1", fontWeight: 700 }}>{s.conto || "—"}</td>
                      <td style={{ fontSize: 10, color: "#555" }}>{s.descrizione_conto || <span style={{ color: "#bbb" }}>seleziona voce</span>}</td>
                      <td style={{ textAlign: "right", fontFamily: "monospace", fontWeight: 800, background: "#fff8e1" }}>{fmt(s.quota)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "#263238", color: "white", fontWeight: 800 }}>
                    <td colSpan={2} style={{ textAlign: "right" }}>BASE 1000</td>
                    <td style={{ textAlign: "right", fontFamily: "monospace" }}>{Number(preview.simulazione.reduce((s,p)=>s+p.base_1000,0)).toLocaleString("it-IT")}</td>
                    <td colSpan={2} style={{ textAlign: "right" }}>TOTALE QUOTE</td>
                    <td style={{ textAlign: "right", fontFamily: "monospace", background: "#1b5e20" }}>{fmt(preview.totale_quote)}</td>
                  </tr>
                </tfoot>
              </table>
              {Math.abs(preview.totale_quote - Number(importo)) > 0.01 && (
                <div style={{ marginTop: 6, padding: 6, background: "#fff3e0", color: "#e65100", fontSize: 11, fontWeight: 700 }}>
                  ⚠ Differenza arrotondamento: € {fmt(Number(importo) - preview.totale_quote)} — verrà assorbita dall&apos;ultima riga.
                </div>
              )}
            </Step>
          )}
          <div style={{ marginTop: 8, display: "flex", gap: 10, alignItems: "center", paddingTop: 10, borderTop: "1px solid #eee" }}>
            <button onClick={esegui} disabled={!canExecute || eseguendo} data-testid="pn-rip-esegui"
              style={{ padding: "10px 30px", background: canExecute ? "#1b5e20" : "#ccc", color: "white", border: "none", fontSize: 14, fontWeight: 800, cursor: canExecute ? "pointer" : "not-allowed" }}>
              {eseguendo ? "Esecuzione..." : "✅ ESEGUI E GENERA RIGHE"}
            </button>
            <span style={{ fontSize: 11, color: "#666" }}>
              Verranno generate <b>{preview?.simulazione?.length || 4} righe</b> (una per settore) sulla voce <b>[{conto || "—"}]</b>.
              Eventuali righe già presenti per la stessa voce verranno sovrascritte.
            </span>
          </div>
        </div>
      </div>
      <VocePicker open={pickerOpen} value={conto} onClose={() => setPickerOpen(false)}
        onSelect={v => { setConto(v.id_conto); setVoceDescr(v.descrizione); setPickerOpen(false); }} />
    </div>
  );
}
